<template>
  <article class="object-teaser">
    <div class="object-teaser-inner" data-same-height>
      <h3 class="object-teaser-title">
        <a :href="objectUrl" @click="persist">{{
          object.titles.map((title) => title.text)[0]
        }}</a>
      </h3>

      <footer class="object-teaser-meta">
        <div class="object-people">
          {{ uniquePeople.map((person) => person.name).join(', ') }}
        </div>
        <div class="object-dating">
          <div v-for="(dating, key) in object.datings" :key="key">
            <template v-if="dating.prefix">{{ dating.prefix }}&nbsp;</template>
            <template v-if="dating.fullFrom"
              >{{ dating.fullFrom
              }}<template v-if="dating.fullTo">
                &ndash; {{ dating.fullTo }}</template
              >
            </template>
            <template v-if="dating.certainty">{{ dating.certainty }}</template>
            <template v-if="dating.suffix">{{ dating.suffix }}</template>
          </div>
        </div>
        <span hidden data-inventory-number v-if="object.inventoryNumber">{{
          object.inventoryNumber
        }}</span>
      </footer>
    </div>

    <div class="object-teaser-image">
      <div class="relative">
        <picture v-if="preferredImage.preview">
          <img
            :src="`${baseUrl}/${preferredImage.preview.webp}`"
            :width="preferredImage.preview.width"
            :height="preferredImage.preview.height"
            alt=""
            class="pointer-events-none"
            loading="lazy"
          />
        </picture>
        <span class="hidden" data-caption v-if="preferredImage.caption">{{
          preferredImage.caption
        }}</span>

        <button
          type="button"
          class="button-round button-group-child absolute bottom-0 left-0 mb-10px ml-10px p-2 text-black"
          aria-pressed="false"
          v-star="parseInt(object.id, 10)"
          v-cloak
        >
          <span class="sr-only">{{ t('add_to_my_album') }}</span>
          <svg
            class="h-4 w-4"
            focusable="false"
            aria-hidden="true"
            viewBox="0 0 54 54"
            width="16"
            height="16"
          >
            <path
              d="M27,10.39L31.75,20L32.68,21.88L34.76,22.18L45.37,23.73L37.69,31.21L36.19,32.68L36.54,34.75L38.35,45.33L28.87,40.33L27.01,39.33L25.14,40.33L15.65,45.33L17.46,34.75L17.81,32.68L16.31,31.21L8.63,23.74L19.24,22.19L21.32,21.89L22.25,20L27,10.39M27,1.39L18.67,18.23L0,20.94L13.51,34.09L10.34,52.65L27,43.89L43.66,52.65L40.48,34.09L54,20.94L35.33,18.23L27,1.35L27,1.39Z"
              style="fill-rule: nonzero"
            />
            <path
              d="M27,10.39L31.75,20L32.68,21.88L34.76,22.18L45.37,23.73L37.69,31.21L36.19,32.68L36.54,34.75L38.35,45.33L28.87,40.33L27.01,39.33L25.14,40.33L15.65,45.33L17.46,34.75L17.81,32.68L16.31,31.21L8.63,23.74L19.24,22.19L21.32,21.89L22.25,20L27,10.39"
              style="fill-rule: nonzero"
              fill="none"
              class="check-mark"
            />
          </svg>
        </button>
      </div>
    </div>
  </article>
</template>

<i18n>
{
  "en": {
    "add_to_my_album": "Add to my album"
  },
  "de": {
    "add_to_my_album": "Zu meinem Album hinzufügen"
  }
}
</i18n>
<script lang="ts">
import { computed, defineComponent } from 'vue';
import { lang } from '../utils/utils';
import { sanitizedFilter, sort } from '../utils/filter';
import { useI18n } from 'vue-i18n';

declare const BASE_URL: string;
export default defineComponent({
  props: {
    object: {
      type: Object,
      required: true,
    },
    siblingIds: {
      type: Array<string>,
      required: false,
    },
    index: {
      type: Number,
      required: false,
    },
    persistSearch: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const objectUrl =
      lang === 'de'
        ? `/objekt/${props.object.id}`
        : `/en/object/${props.object.id}`;
    const preferredImage = computed(() => {
      return (
        props.object.multimedia.find((image: any) => image.preferred) ||
        props.object.multimedia[0]
      );
    });

    const uniquePeople = [
      ...new Set(
        props.object.people.filter(
          (value: any, index: number, self: any) =>
            self.map((x: any) => x.personId).indexOf(value.personId) === index
        )
      ),
    ];

    const persist = () => {
      persistSiblings();
      persistSearch();
    };

    const persistSiblings = () => {
      if (props.siblingIds) {
        sessionStorage.setItem('siblingIds', JSON.stringify(props.siblingIds));
        sessionStorage.removeItem('searchUrl');
        sessionStorage.removeItem('index');
      } else {
        sessionStorage.removeItem('siblingIds');
      }
    };

    const persistSearch = () => {
      if (props.persistSearch) {
        sessionStorage.setItem('searchUrl', document.location.href);
        sessionStorage.setItem('filter', JSON.stringify(sanitizedFilter.value));
        sessionStorage.setItem('sort', sort.value);
        sessionStorage.removeItem('siblingIds');
      } else {
        sessionStorage.removeItem('searchUrl');
      }

      if (typeof props.index !== 'undefined') {
        sessionStorage.setItem('index', props.index);
      } else {
        sessionStorage.removeItem('index');
      }
    };

    return {
      objectUrl,
      preferredImage,
      uniquePeople,
      baseUrl: BASE_URL,
      persist,
      ...useI18n(),
    };
  },
});
</script>
